import { Link } from "react-router-dom";
import { Globe2, Smartphone, Users, ArrowRight } from "lucide-react";

/* The Brand Contract explainer — one place that spells out the three
   names in the TravelerPulse / SocialPulse family so travellers stop seeing
   competing branding on every screen.

   Rendered on /travel-hub landing (signed-out) and inside the More tab. */
export default function BrandContract({ compact = false }) {
  const items = [
    {
      icon: Globe2, tint: "bg-sky-100 text-sky-700",
      title: "TravelerPulse.pro",
      role: "The public travel platform",
      body: "Discover airports, airlines, destinations, guides, lounges and tools — free for every traveller.",
    },
    {
      icon: Smartphone, tint: "bg-emerald-100 text-emerald-700",
      title: "My Travel Hub",
      role: "The installed personal app",
      body: "Your private trips, documents, journey timeline, memories and private vaults. Lives on your phone.",
    },
    {
      icon: Users, tint: "bg-fuchsia-100 text-fuchsia-700",
      title: "SocialPulse",
      role: "Your identity across the network",
      body: "One secure sign-in that works across TravelerPulse, HotelsPulse and the whole SocialPulse family.",
    },
  ];
  if (compact) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="brand-contract-compact">
        <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-emerald-700 mb-2">Three names, one travel home</div>
        <ul className="space-y-2 text-sm text-slate-700">
          {items.map((it) => (
            <li key={it.title} className="flex items-start gap-2.5">
              <span className={`w-7 h-7 rounded-xl grid place-items-center shrink-0 ${it.tint}`}><it.icon className="w-4 h-4" /></span>
              <span><b className="text-slate-900">{it.title}</b> — <span className="text-slate-600">{it.role}.</span></span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  return (
    <section className="max-w-5xl mx-auto px-5 lg:px-8 py-10" data-testid="brand-contract">
      <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-emerald-700 mb-2">How these fit together</div>
      <h2 className="font-serif font-800 text-2xl sm:text-3xl text-slate-900">Three names, one travel home</h2>
      <p className="text-slate-600 mt-2 max-w-2xl">You'll see three brands as you move through the site. They work together — here's what each one does:</p>
      <div className="grid md:grid-cols-3 gap-4 mt-6">
        {items.map((it) => (
          <div key={it.title} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`brand-${it.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className={`w-10 h-10 rounded-xl grid place-items-center ${it.tint}`}><it.icon className="w-5 h-5" /></div>
            <div className="font-serif font-800 text-lg text-slate-900 mt-3">{it.title}</div>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mt-0.5">{it.role}</div>
            <p className="text-sm text-slate-600 mt-2">{it.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-6">
        <Link to="/install" className="inline-flex items-center gap-1.5 text-emerald-700 font-700 text-sm hover:underline" data-testid="brand-install-link">
          Install the full experience on your phone <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}
